import { useMemo } from "react"

import isChromatic from "chromatic/isChromatic"
import { BarChart as EChartsBarChart } from "echarts/charts"
import {
  DataZoomComponent,
  DatasetComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from "echarts/components"
import * as echarts from "echarts/core"
import merge from "lodash/merge"
import { renderToString } from "react-dom/server"
import { useMatch } from "react-router"

import { Chart, ChartProps } from "./Chart"
import { Divider } from "./Divider"

echarts.use([
  EChartsBarChart,
  GridComponent,
  TooltipComponent,
  DatasetComponent,
  DataZoomComponent,
  LegendComponent,
])

export type BarChartProps = ChartProps

export function BarChart({
  title,
  options,
  icon,
  actions,
  className,
  isEmpty = false,
}: BarChartProps) {
  const isPdf = useMatch("/pdf/*")
  const eChartsOptions = useMemo(
    () =>
      merge(
        {
          tooltip: {
            className: /*tw*/ "!shadow !rounded-md",
            borderWidth: 0,
            appendToBody: true,
            trigger: "axis",
            formatter: (
              data: {
                axisValue: string | number
                value: string | number
                color: string
                seriesName: string
              }[],
            ) => {
              return renderToString(
                <BarTooltip
                  data={data.map((item) => ({
                    color: item.color,
                    label: item.seriesName,
                    value: `${
                      options?.tooltip?.valueFormatter?.(item.value) ?? item.value
                    }`,
                    axisValue: `${
                      options?.tooltip?.axisValueFormatter?.(item.axisValue) ??
                      item.axisValue
                    }`,
                  }))}
                />,
              )
            },
          },
          series: options?.series?.map((opts: any) => ({
            ...opts,
            type: "bar",
            animation: !isChromatic() && !isPdf,
          })),
        },
        { ...options, series: undefined },
      ),
    [isPdf, options],
  )

  return (
    <Chart
      className={className}
      title={title}
      icon={icon}
      actions={actions}
      isEmpty={isEmpty}
      options={eChartsOptions}
    />
  )
}

interface BarTooltipProps {
  data: {
    color: string
    label: string
    value: string
    axisValue: string
  }[]
}

function BarTooltip({ data }: BarTooltipProps) {
  return (
    <div className="flex flex-col">
      {data.map((item) => (
        <div key={item.label} className="flex items-center gap-2">
          <div
            className="mr-1 h-3 w-3 rounded-full"
            style={{ backgroundColor: item.color }}
          />
          <div className="text-body1 text-grey-500">{item.label}:</div>
          <div className="text-body1 text-grey-900">{item.value}</div>
        </div>
      ))}
      <Divider className="my-2" />
      <div className="text-body1 text-grey-500">{data[0].axisValue}</div>
    </div>
  )
}
